import React from 'react';


export const BREADCRUMB_THEME = {
  /* ── Layout ─────────────────────────────────────────────────────────────── */
  wrap:            'flex flex-wrap items-end justify-between gap-3',
  actionWrap:      'flex items-center gap-2',

  /* ── Breadcrumb trail ───────────────────────────────────────────────────── */
  trailSize:       'text-xs',
  trailWeight:     '',
  trailColor:      'text-gray-400',
  trailLastColor:  'text-gray-500',        // the current page in the trail
  trailLinkHover:  'hover:text-gray-600',
  trailSeparator:  '/',
  trailSepSpacing: 'mx-1',

  /* ── Title ──────────────────────────────────────────────────────────────── */
  titleSize:       'text-2xl',
  titleWeight:     'font-bold',
  titleColor:      'text-gray-900',
  titleSpacing:    'mt-0.5',

  /* ── Subtitle ───────────────────────────────────────────────────────────── */
  subtitleSize:    'text-xs',
  subtitleWeight:  '',
  subtitleColor:   'text-gray-400',
  subtitleSpacing: 'mt-1',

  /* ── Action buttons ─────────────────────────────────────────────────────── */
  btnBase:         'flex items-center gap-2 h-9 rounded-lg text-xs font-semibold transition-colors',
  btnPadding:      'px-4',
  btnGhostPadding: 'px-3',
  btnDisabled:     'disabled:opacity-40 disabled:cursor-not-allowed',
  btnPrimary:      'bg-primary-600 text-white hover:bg-primary-700',
  btnExport:       'bg-emerald-600 text-white hover:bg-emerald-700',
  btnGhost:        'bg-gray-100 text-gray-600 hover:bg-gray-200',
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  Nothing below needs editing to restyle — it all reads the theme.
 * ═══════════════════════════════════════════════════════════════════════════ */

const cx = (...parts) => parts.filter(Boolean).join(' ');

const T = BREADCRUMB_THEME;

/** Ready-made action buttons, so every page header's buttons match. */
export const BREADCRUMB_ACTION_BTN = cx(T.btnBase, T.btnPadding, T.btnPrimary, T.btnDisabled);
export const BREADCRUMB_EXPORT_BTN = cx(T.btnBase, T.btnPadding, T.btnExport, T.btnDisabled);
export const BREADCRUMB_GHOST_BTN  = cx(T.btnBase, T.btnGhostPadding, T.btnGhost, T.btnDisabled);

/**
 * Breadcrumb — the common page header for the whole project.
 *
 * Design lifted from the Overtime Details page header. It exists so no page is
 * left without a breadcrumb: the trail is part of the header, not something
 * each page remembers to add. components/PageHeader/PageHeader.js is untouched.
 *
 *   <Breadcrumb
 *     trail={['Overtime Management', 'Overtime Details']}
 *     title="Overtime Details"
 *     action={
 *       <button className={BREADCRUMB_EXPORT_BTN} onClick={handleExport}>
 *         <FileSpreadsheet size={15} /> Export to Excel
 *       </button>
 *     }
 *   />
 *
 *   // trail items may be links or handlers:
 *   trail={[{ label: 'Payroll', href: '/payroll' }, 'Payslips']}
 */
export default function Breadcrumb({
  trail = [],
  title,
  subtitle,
  action,
  theme,
  className = '',
}) {
  const t = { ...BREADCRUMB_THEME, ...theme };
  const items = trail.filter(Boolean);

  return (
    <div className={cx(t.wrap, className)}>
      <div>
        {items.length > 0 && (
          <p className={cx(t.trailSize, t.trailWeight, t.trailColor)}>
            {items.map((item, i) => {
              const isObj = typeof item === 'object' && item !== null;
              const label = isObj ? item.label : item;
              const href = isObj ? item.href : undefined;
              const onClick = isObj ? item.onClick : undefined;
              const last = i === items.length - 1;

              const node = href || onClick ? (
                <a href={href || undefined} onClick={onClick}
                   className={cx(t.trailLinkHover, 'transition-colors cursor-pointer')}>
                  {label}
                </a>
              ) : (
                <span className={last ? t.trailLastColor : undefined}>{label}</span>
              );

              return (
                <React.Fragment key={`${label}-${i}`}>
                  {node}
                  {!last && <span className={t.trailSepSpacing}>{t.trailSeparator}</span>}
                </React.Fragment>
              );
            })}
          </p>
        )}

        {title && (
          <h2 className={cx(t.titleSize, t.titleWeight, t.titleColor, t.titleSpacing)}>{title}</h2>
        )}
        {subtitle && (
          <p className={cx(t.subtitleSize, t.subtitleWeight, t.subtitleColor, t.subtitleSpacing)}>
            {subtitle}
          </p>
        )}
      </div>

      {action && <div className={t.actionWrap}>{action}</div>}
    </div>
  );
}